import React from "react";
import {
  NumberInput,
  Radio,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { LoyaltyPreset } from "../../../types/loyalty";
import SelectableCard from "./SelectableCard";
import {
  PersonalizeDraft,
  EARN_POINTS,
  PRESET_LABELS,
  presetDescription,
} from "./types";

interface PersonalizeFormProps {
  value: PersonalizeDraft;
  onChange: (value: PersonalizeDraft) => void;
  /** Per-type earn rates (set in the value step) — drive the preset copy. */
  servicesRate?: number | string;
  productsRate?: number | string;
  /** Hide the step heading when embedded in a Settings edit modal. */
  showHeading?: boolean;
}

const PRESET_ORDER: LoyaltyPreset[] = ["both", "retail", "service"];

/** Step 1 — Personalize your program (loyalty FE1 §2.1). Aug-25 punch list:
 *  expiration is now a Yes/No radio group with a conditional days input;
 *  the "deducted from commissions" question is removed (always false). */
export default function PersonalizeForm({
  value,
  onChange,
  servicesRate = EARN_POINTS,
  productsRate = EARN_POINTS,
  showHeading = true,
}: PersonalizeFormProps) {
  const rewardsExpire = value.expirationPolicy !== "none";

  return (
    <Stack gap="lg">
      {showHeading && (
        <Stack gap={4}>
          <Title order={2}>Personalize your program</Title>
          <Text size="sm" c="dimmed">
            Select a preset to get started and decide if the rewards expire. You
            can always change this later.
          </Text>
        </Stack>
      )}

      <Stack gap="xs">
        <Text fw={600} size="sm">
          Choose how clients get points:
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" role="radiogroup">
          {PRESET_ORDER.map((preset) => (
            <SelectableCard
              key={preset}
              title={PRESET_LABELS[preset]}
              lines={[
                presetDescription(preset, servicesRate, productsRate),
                "Tips not included",
              ]}
              selected={value.preset === preset}
              onSelect={() => onChange({ ...value, preset })}
            />
          ))}
        </SimpleGrid>
      </Stack>

      <Radio.Group
        label="Do their rewards ever expire?"
        value={rewardsExpire ? "yes" : "no"}
        onChange={(next) =>
          onChange({
            ...value,
            expirationPolicy: next === "yes" ? "rolling" : "none",
          })
        }
      >
        <Stack gap="xs" mt="xs">
          <Radio value="no" label="No" color="var(--brand-teal)" />
          <Radio value="yes" label="Yes" color="var(--brand-teal)" />
        </Stack>
      </Radio.Group>

      {rewardsExpire && (
        <Stack gap="xs">
          <Text fw={600} size="sm">
            How many days until they expire?
          </Text>
          <NumberInput
            aria-label="How many days until they expire?"
            value={value.expirationDays}
            onChange={(raw) => {
              const normalized =
                typeof raw === "number" ? Math.max(1, Math.floor(raw)) : raw;
              onChange({ ...value, expirationDays: normalized });
            }}
            min={1}
            clampBehavior="none"
            allowDecimal={false}
            allowNegative={false}
            hideControls
            w={200}
            suffix=" days"
          />
        </Stack>
      )}
    </Stack>
  );
}
